"use client";
import {
  TimeField as AriaTimeField,
  TimeFieldProps as AriaTimeFieldProps,
  DateInput,
  DateSegment,
  TimeValue,
  ValidationResult,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps } from "../../utils";
import type { Size } from "../../utils/controlStyles";
import { BaseInputProps, Description, FieldError, getInputStateStyles, InputWrapper, Label } from "../Field";

/**
 * TimeField
 *
 * A segmented time input with Edges styling. Matches the API and styling
 * of TextField and DateField for consistency across form controls.
 */
export interface TimeFieldProps
  extends Omit<AriaTimeFieldProps<TimeValue>, "isRequired" | "size" | "className">,
    Omit<BaseInputProps, "transparent" | "isClearable" | "onClear"> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  tooltip?: string;
  isRequired?: boolean;
  className?: string;
  validationResult?: ValidationResult;
  /**
   * Whether to reserve space for error messages to prevent layout shift
   * @default true
   */
  reserveErrorSpace?: boolean;
  /**
   * Where to display the description text
   * - "below": Show below the field (default)
   * - "tooltip": Show as a tooltip on the label icon
   * - "inline": Show inline with the label
   * - "hidden": Don't display the description
   * @default "below"
   */
  descriptionPlacement?: "below" | "tooltip" | "inline" | "hidden";
}

function getSegmentStyles({
  isPlaceholder,
  isDisabled,
  size = "md",
}: {
  isPlaceholder?: boolean;
  isDisabled?: boolean;
  size?: Size;
}) {
  // Size styles with mobile font-size fix
  // iOS Safari zooms in on inputs with font-size < 16px when focused.
  // On mobile, we use text-base (16px) for sm/md sizes to prevent auto-zoom.
  // On desktop (md: breakpoint), we restore the original smaller sizes.
  const sizeStyles = {
    sm: "text-base md:text-xs",
    md: "text-base md:text-sm",
    lg: "text-base",
    xl: "text-lg",
  };

  return twMerge(
    // Base
    "inline-block box-content px-0.5 rounded outline-none tabular-nums text-center transition-colors",
    sizeStyles[size],
    // Text color
    isPlaceholder ? "text-text-placeholder italic" : "text-text-body",
    // Disabled state
    isDisabled ? "text-text-disabled cursor-not-allowed" : "cursor-text",
    // Hover/focus
    !isDisabled && "hover:bg-background-hover",
    "data-[focused]:bg-background-selected data-[focused]:text-text-body"
  );
}

/**
 * Renders an Edges TimeField with label, description, validation states,
 * and segmented time input.
 */
export function TimeField({
  label,
  description,
  errorMessage,
  size = "md",
  tooltip,
  isRequired,
  isDisabled,
  isInvalid,
  reserveErrorSpace = true,
  validationResult,
  className,
  descriptionPlacement = "below",
  ...props
}: TimeFieldProps) {
  return (
    <AriaTimeField
      {...props}
      isDisabled={isDisabled}
      isRequired={isRequired}
      isInvalid={isInvalid}
      className={composeTailwindRenderProps(className, "flex flex-col gap-1")}
    >
      {label && (
        <Label
          size={size}
          tooltip={tooltip}
          description={
            descriptionPlacement === "tooltip" || descriptionPlacement === "inline" ? description : undefined
          }
          descriptionPlacement={descriptionPlacement}
          isRequired={isRequired}
        >
          {label}
        </Label>
      )}

      <InputWrapper>
        <DateInput
          className={twMerge(
            getInputStateStyles({
              isInvalid,
              isDisabled,
            }),
            // Padding and layout
            "px-3 py-2 flex items-center gap-0.5 w-full"
          )}
        >
          {(segment) => (
            <DateSegment
              segment={segment}
              className={getSegmentStyles({
                isPlaceholder: segment.isPlaceholder,
                isDisabled: !segment.isEditable || isDisabled,
                size,
              })}
            />
          )}
        </DateInput>
      </InputWrapper>

      {reserveErrorSpace ? (
        <div className="mt-1 min-h-[20px]">
          {typeof errorMessage === "string" ? (
            <FieldError size={size}>{errorMessage}</FieldError>
          ) : typeof errorMessage === "function" && validationResult ? (
            <FieldError size={size}>{errorMessage(validationResult)}</FieldError>
          ) : description && descriptionPlacement === "below" ? (
            <Description size={size}>{description}</Description>
          ) : null}
        </div>
      ) : (
        <>
          {typeof errorMessage === "string" ? (
            <FieldError size={size} className="mt-1">
              {errorMessage}
            </FieldError>
          ) : typeof errorMessage === "function" && validationResult ? (
            <FieldError size={size} className="mt-1">
              {errorMessage(validationResult)}
            </FieldError>
          ) : description && descriptionPlacement === "below" ? (
            <Description size={size} className="mt-1">
              {description}
            </Description>
          ) : null}
        </>
      )}
    </AriaTimeField>
  );
}
